/* ============================================================================
 * db.jsx — Supabase client wrapper (ES modules)
 * ========================================================================= */

import { createClient } from '@supabase/supabase-js'
import { reportError } from './errors.jsx'

let _client = null;

export function getClient() {
  if (_client) return _client;
  const cfg = window.WYF_CONFIG;
  if (!cfg || !cfg.SUPABASE_URL || !cfg.SUPABASE_ANON_KEY) {
    console.warn("[WYF-CFG-001] Supabase not configured.");
    return null;
  }
  _client = createClient(cfg.SUPABASE_URL, cfg.SUPABASE_ANON_KEY, {
    auth: { persistSession: true, autoRefreshToken: true, storageKey: "wyf-auth" },
  });
  return _client;
}


export function classifyError(err) {
  if (!err) return "WYF-DB-107";
  const code = err.code || "";
  const msg = (err.message || "").toLowerCase();
  if (msg.includes("network") || msg.includes("fetch")) return "WYF-NET-001";
  if (msg.includes("timeout")) return "WYF-NET-003";
  if (code === "23505" || msg.includes("duplicate")) return "WYF-DB-101";
  if (code === "23503" || msg.includes("foreign key")) return "WYF-DB-102";
  if (code === "23502" || msg.includes("not-null")) return "WYF-DB-103";
  if (code === "PGRST116" || msg.includes("not found")) return "WYF-DB-104";
  if (msg.includes("row-level security") || msg.includes("permission")) return "WYF-DB-100";
  if (msg.includes("rpc") || msg.includes("function")) return "WYF-DB-105";
  return "WYF-DB-107";
}

export async function selectAll(table, options) {
  options = options || {};
  const client = getClient();
  if (!client) return { data: null, error: { code: "WYF-CFG-001" } };
  let q = client.from(table).select(options.columns || "*");
  if (options.eq) for (const [k, v] of Object.entries(options.eq)) q = q.eq(k, v);
  if (options.order) q = q.order(options.order.column, { ascending: options.order.ascending !== false });
  if (options.limit) q = q.limit(options.limit);
  if (options.signal) q = q.abortSignal(options.signal);
  const TIMEOUT_MS = 20000;
  const timeoutPromise = new Promise((_, rej) =>
    setTimeout(() => rej(Object.assign(new Error("Request timed out."), { _timeout: true })), TIMEOUT_MS)
  );
  let data, error;
  try {
    ({ data, error } = await Promise.race([q, timeoutPromise]));
  } catch (err) {
    if (err.name === 'AbortError') return { data: null, error: null };
    const code = err._timeout ? "WYF-NET-003" : classifyError(err);
    reportError({ code, where: `DB / select ${table}`, message: err.message, detail: String(err) });
    return { data: null, error: err };
  }
  if (error) {
    reportError({ code: classifyError(error), where: `DB / select ${table}`, message: error.message, detail: JSON.stringify(error) });
    return { data: null, error };
  }
  return { data, error: null };
}

export async function insert(table, payload) {
  const client = getClient();
  if (!client) return { data: null, error: { code: "WYF-CFG-001" } };
  const { data, error } = await client.from(table).insert(payload).select().single();
  if (error) reportError({
    code: classifyError(error),
    where: `DB / insert ${table}`,
    message: error.message,
    detail: JSON.stringify(payload),
  });
  return { data, error };
}

export async function update(table, id, patch) {
  const client = getClient();
  if (!client) return { data: null, error: { code: "WYF-CFG-001" } };
  const { data, error } = await client.from(table).update(patch).eq("id", id).select().single();
  if (error) reportError({
    code: classifyError(error),
    where: `DB / update ${table}`,
    message: error.message,
    detail: JSON.stringify({ id, patch }),
  });
  return { data, error };
}

export async function softDelete(table, id) {
  const client = getClient();
  if (!client) return { data: null, error: { code: "WYF-CFG-001" } };
  const { data, error } = await client.from(table).update({ active: false, deleted_at: new Date().toISOString() }).eq("id", id);
  if (error) reportError({
    code: classifyError(error),
    where: `DB / softDelete ${table}`,
    message: error.message,
    detail: String(id),
  });
  return { data, error };
}

export async function rpc(fn, params, signal) {
  const client = getClient();
  if (!client) return { data: null, error: { code: "WYF-CFG-001" } };
  const TIMEOUT_MS = 20000;
  const timeoutPromise = new Promise((_, rej) =>
    setTimeout(() => rej(Object.assign(new Error("Request timed out."), { _timeout: true })), TIMEOUT_MS)
  );
  let data, error;
  try {
    let call = client.rpc(fn, params || {});
    if (signal) call = call.abortSignal(signal);
    ({ data, error } = await Promise.race([call, timeoutPromise]));
  } catch (err) {
    if (err.name === 'AbortError') return { data: null, error: null };
    reportError({ code: err._timeout ? "WYF-NET-003" : classifyError(err), where: `RPC / ${fn}`, message: err.message, detail: String(err) });
    return { data: null, error: err };
  }
  if (error) reportError({
    code: classifyError(error) === "WYF-DB-107" ? "WYF-DB-105" : classifyError(error),
    where: `RPC / ${fn}`,
    message: error.message,
    detail: JSON.stringify(params),
  });
  return { data, error };
}

/* Domain helpers */
import { formatPhone, BUSINESS_CONTACT_TYPES } from './utils.jsx'

function contactRpcParams(f) {
  return {
    p_name: f.name,
    p_email: f.email || null,
    p_phone: f.phone ? formatPhone(f.phone) : null,
    p_birthday: f.birthday || null,
    p_address: f.address || null,
    p_type: f.type,
    p_source: f.source || null,
    p_lifecycle_status: f.lifecycle_status || null,
    p_reseller_discount_pct: BUSINESS_CONTACT_TYPES.includes(f.type) ? (f.reseller_discount_pct || null) : null,
    p_business_name: BUSINESS_CONTACT_TYPES.includes(f.type) ? (f.business_name || null) : null,
    p_tin: BUSINESS_CONTACT_TYPES.includes(f.type) ? (f.tin || null) : null,
    p_billing_address: BUSINESS_CONTACT_TYPES.includes(f.type) ? (f.billing_address || null) : null,
    p_contact_person: BUSINESS_CONTACT_TYPES.includes(f.type) ? (f.contact_person || null) : null,
    p_price_tier: f.price_tier || null,
    p_payment_terms: BUSINESS_CONTACT_TYPES.includes(f.type) ? (f.payment_terms || null) : null,
    p_credit_limit: BUSINESS_CONTACT_TYPES.includes(f.type) ? (f.credit_limit || null) : null,
    p_notes: f.notes || null,
  };
}

export const Contacts = {
  list:   (signal) => selectAll("contacts_with_stats", { order: { column: "created_at", ascending: false }, signal }),
  create: (form) => rpc("create_contact", contactRpcParams(form)),
  update: (id, form) => rpc("update_contact", { p_id: id, ...contactRpcParams(form) }),
  remove: (id) => rpc("soft_delete_contact", { p_id: id }),
};

export const Products = {
  list:        (signal) => selectAll("products_with_pricing", { order: { column: "sort_order" }, signal }),
  ingredients: (productId, signal) => selectAll("product_ingredients_list", { eq: { product_id: productId }, signal }),
  upsertIngredient: (productId, itemId, qtyPerTin, unit, notes, ingredientType = 'base_flan') => rpc("upsert_product_ingredient", { p_product_id: productId, p_item_id: itemId, p_qty_per_tin: qtyPerTin, p_unit: unit, p_notes: notes || null, p_ingredient_type: ingredientType }),
  removeIngredient: (id) => rpc("soft_delete_product_ingredient", { p_id: id }),
  create: (f) => rpc("create_product", {
    p_name: f.name, p_category: f.category, p_active: f.active ?? true,
    p_sort_order: f.sort_order || 0, p_image_url: f.image_url || null, p_notes: f.notes || null,
    p_price_retail: f.price_retail || null, p_price_reseller: f.price_reseller || null,
    p_price_franchise: f.price_franchise || null,
  }),
  update: (id, f) => rpc("update_product", {
    p_id: id, p_name: f.name, p_category: f.category, p_active: f.active ?? true,
    p_sort_order: f.sort_order || 0, p_image_url: f.image_url || null, p_notes: f.notes || null,
    p_price_retail: f.price_retail || null, p_price_reseller: f.price_reseller || null,
    p_price_franchise: f.price_franchise || null,
  }),
  remove: (id) => rpc("soft_delete_product", { p_id: id }),
  flavorSets:      (productId, signal) => selectAll("product_flavor_sets_list", { eq: { product_id: productId }, order: { column: "sort_order" }, signal }),
  addFlavorSet:    (productId, setName, flavors, sortOrder, notes) => insert("product_flavor_sets", { product_id: productId, set_name: setName, flavors, sort_order: sortOrder || 0, notes: notes || null }),
  updateFlavorSet: (id, patch) => update("product_flavor_sets", id, patch),
  removeFlavorSet: (id) => update("product_flavor_sets", id, { active: false }),
};

export const Orders = {
  list:        (signal) => selectAll("orders_list", { order: { column: "created_at", ascending: false }, signal }),
  items:       (orderId, signal) => selectAll("order_items_list", { eq: { order_id: orderId }, signal }),
  allItems:    (signal) => selectAll("order_items_list", { order: { column: "created_at", ascending: false }, signal }),
  payments:    (orderId, signal) => selectAll("order_payments", { eq: { order_id: orderId, active: true }, order: { column: "paid_at", ascending: false }, signal }),
  refunds:     (orderId, signal) => selectAll("order_refunds", { eq: { order_id: orderId }, order: { column: "refunded_at", ascending: false }, signal }),
  create:      (f) => rpc("create_order", f),
  cancel:      (id, reason) => rpc("cancel_order", { p_id: id, p_reason: reason }),
  updateStatus: (id, status) => rpc("update_order_status", { p_id: id, p_status: status }),
  addItem:     (p) => rpc("add_order_item", p),
  updateItem:  (p) => rpc("update_order_item", p),
  removeItem:  (id) => rpc("remove_order_item", { p_id: id }),
  addPayment:    (p) => rpc("add_order_payment", p),
  removePayment: (id) => rpc("soft_delete_order_payment", { p_id: id }),
  addRefund:     (p) => rpc("add_order_refund", p),
  remove:        (id) => rpc("soft_delete_order", { p_id: id }),
  updateDetails: (id, form) => rpc("update_order", {
    p_id: id,
    p_status: null,
    p_delivery_method: form.delivery_method || null,
    p_delivery_date: form.delivery_date || null,
    p_delivery_address: form.delivery_address || null,
    p_pickup_location: form.pickup_location || null,
    p_meetup_location: form.meetup_location || null,
    p_meetup_datetime: form.meetup_datetime || null,
    p_rider_id: form.rider_id || null,
    p_notes: form.notes || null,
    p_or_number: form.or_number || null,
  }),
};

export const Expenses = {
  receipts:   (signal) => selectAll("receipts_list", { order: { column: "date", ascending: false }, signal }),
  lines:      (receiptId, signal) => selectAll("line_items_list", { eq: { receipt_id: receiptId }, signal }),
  categories: (signal) => selectAll("expense_categories", { eq: { active: true }, order: { column: "sort_order" }, signal }),
  itemsLookup:(signal) => selectAll("items", { eq: { active: true }, order: { column: "name" }, signal }),
  removeReceipt: (id) => rpc("soft_delete_receipt", { p_id: id }),
  addLine:    (p) => rpc("add_expense_item", {
    p_receipt_id: p.receipt_id, p_item_id: p.item_id || null,
    p_item_name_snapshot: p.item_name_snapshot, p_category_id: p.category_id || null,
    p_quantity: p.quantity, p_unit: p.unit || null, p_unit_cost: p.unit_cost,
    p_business_or_personal: p.business_or_personal || "business", p_notes: p.notes || null,
  }),
  updateLine: (p) => rpc("update_expense_item", {
    p_item_id: p.id, p_item_ref_id: p.item_id || null,
    p_item_name_snapshot: p.item_name_snapshot, p_category_id: p.category_id || null,
    p_quantity: p.quantity, p_unit: p.unit || null, p_unit_cost: p.unit_cost,
    p_business_or_personal: p.business_or_personal || "business", p_notes: p.notes || null,
  }),
  removeLine: (id) => rpc("soft_delete_expense_item", { p_item_id: id }),
  allLines:   (signal) => selectAll("line_items_list", { order: { column: "created_at", ascending: false }, signal }),
  totalBusiness: async (signal) => {
    const client = getClient();
    if (!client) return { data: null, error: { code: "WYF-CFG-001" } };
    let q = client.from("expense_items")
      .select("subtotal")
      .eq("business_or_personal", "business")
      .eq("active", true);
    if (signal) q = q.abortSignal(signal);
    const { data, error } = await q;
    if (error) return { data: null, error };
    const total = (data || []).reduce((s, r) => s + Number(r.subtotal || 0), 0);
    return { data: total, error: null };
  },
};

export const Production = {
  batches:     (signal) => selectAll("batches_list", { order: { column: "date_made", ascending: false }, signal }),
  ingredients:    (batchId, signal) => selectAll("production_ingredients_list", { eq: { batch_id: batchId }, signal }),
  allIngredients: (signal) => selectAll("production_ingredients_list", { order: { column: "created_at", ascending: false }, signal }),
  items:       (signal) => selectAll("items", { eq: { active: true }, order: { column: "name" }, signal }),
  costing:     (signal) => selectAll("costing_by_batch", { order: { column: "date_made", ascending: false }, signal }),
  create:      (f) => rpc("create_batch", f),
  update:      (id, f) => rpc("update_batch", { p_batch_id: id, ...f }),
  updateStatus:(id, status) => rpc("update_batch_status", { p_id: id, p_status: status }),
  remove:      (id) => rpc("soft_delete_batch", { p_id: id }),
  addIngredient:    (batchId, itemId, qty, unit, notes, qtyStandard, ingredientType) => rpc("add_batch_ingredient", { p_batch_id: batchId, p_item_id: itemId, p_qty_used: qty, p_unit: unit, p_notes: notes || null, p_qty_standard: qtyStandard || null, p_ingredient_type: ingredientType || null }),
  updateIngredient: (ingredientId, qty, unit, notes) => rpc("update_batch_ingredient", { p_ingredient_id: ingredientId, p_qty_used: qty, p_unit: unit, p_notes: notes || null }),
  removeIngredient: (ingredientId) => rpc("remove_batch_ingredient", { p_ingredient_id: ingredientId }),
  linkToOrder:       (batchId, orderId) => rpc("link_batch_to_order", { p_batch_id: batchId, p_order_id: orderId }),
  linkToOrderItem:   (batchId, orderId, orderItemId) => rpc("link_batch_to_order_item", { p_batch_id: batchId, p_order_id: orderId, p_order_item_id: orderItemId }),
  unlinkFromOrder:   (batchId) => rpc("unlink_batch_from_order", { p_batch_id: batchId }),
  markWaste:         (batchId, reason) => rpc("mark_batch_waste", { p_batch_id: batchId, p_reason: reason || null }),
  batchByOrderItem: async (orderItemId) => {
    const client = getClient();
    if (!client) return { data: null };
    return client.from("production_batches")
      .select("id, product_name_snapshot, date_made, qty_produced, unit, order_id, disposition, order_item_id")
      .eq("order_item_id", orderItemId)
      .eq("active", true)
      .maybeSingle();
  },
  pendingBatches: async (signal) => {
    const client = getClient();
    if (!client) return { data: [] };
    let q = client.from("production_batches")
      .select("id, product_name_snapshot, date_made, qty_produced, unit")
      .is("order_id", null)
      .eq("disposition", "pending")
      .eq("active", true)
      .order("date_made", { ascending: false })
      .limit(50);
    if (signal) q = q.abortSignal(signal);
    return q;
  },
  unlinked: async (signal) => {
    const client = getClient();
    if (!client) return { data: [], error: null };
    let q = client.from("production_batches")
      .select("id, product_name_snapshot, date_made, qty_produced, unit")
      .is("order_id", null)
      .eq("disposition", "pending")
      .eq("active", true)
      .order("date_made", { ascending: false })
      .limit(5);
    if (signal) q = q.abortSignal(signal);
    return q;
  },
};

export const Inventory = {
  current:     (signal) => selectAll("inventory_current", { order: { column: "item_name" }, signal }),
  adjustments: (signal) => selectAll("inventory_adjustments", { order: { column: "created_at", ascending: false }, signal }),
};

async function readFnError(error) {
  try {
    const body = await error.context?.json?.();
    if (body?.error) return body.error;
    if (body?.message) return body.message;
  } catch { /* response body already consumed or not JSON */ }
  return error.message || "Edge function error.";
}

export const Users = {
  list:   (signal) => selectAll("app_users", { order: { column: "created_at", ascending: false }, signal }),
  update: (id, patch) => update("app_users", id, patch),
  setActive: (id, active) => update("app_users", id, { active }),
  delete: async (userId) => {
    const client = getClient();
    if (!client) return { data: null, error: { message: "Supabase not configured." } };
    const { data, error } = await client.functions.invoke("delete-user", {
      body: { user_id: userId },
    });
    if (error) {
      const msg = await readFnError(error);
      return { data: null, error: { message: msg } };
    }
    if (data?.error) return { data: null, error: { message: data.error } };
    return { data: data?.data ?? data, error: null };
  },
  revokeSession: async (userId) => {
    const client = getClient();
    if (!client) return { data: null, error: { message: "Supabase not configured." } };
    const { data, error } = await client.functions.invoke("revoke-user-sessions", {
      body: { user_id: userId },
    });
    if (error) {
      const msg = await readFnError(error);
      return { data: null, error: { message: msg } };
    }
    if (data?.error) return { data: null, error: { message: data.error } };
    return { data: data?.data ?? data, error: null };
  },
  invite: async (email, full_name, role) => {
    const client = getClient();
    if (!client) return { data: null, error: { message: "Supabase not configured." } };
    const { data, error } = await client.functions.invoke("invite-user", {
      body: { email, full_name, role },
    });
    if (error) {
      const msg = await readFnError(error);
      return { data: null, error: { message: msg } };
    }
    if (data?.error) return { data: null, error: { message: data.error } };
    return { data: data?.data ?? data, error: null };
  },
};

export const AuditLog = {
  list:      (signal) => selectAll("audit_log", { order: { column: "changed_at", ascending: false }, limit: 200, signal }),
  logins:    (signal) => selectAll("audit_log", { eq: { action: 'login' }, order: { column: "changed_at", ascending: false }, limit: 500, signal }),
  forRecord: (tableName, recordId, signal) => selectAll("audit_log", {
    eq: { table_name: tableName, record_id: recordId },
    order: { column: "changed_at", ascending: false },
    signal,
  }),
};

export const Riders = {
  list: (signal) => selectAll("riders_list", { order: { column: "name" }, signal }),
  availability: (riderId, signal) => selectAll("rider_availability", { eq: { rider_id: riderId }, signal }),
  errands: (riderId, signal) => selectAll("errands_list", { eq: { rider_id: riderId }, order: { column: "date", ascending: false }, signal }),
  deliveries: (riderId, signal) => selectAll("orders_list", { eq: { rider_id: riderId }, order: { column: "delivery_date", ascending: false }, signal }),
  create: (f) => rpc("create_rider", {
    p_name: f.name, p_phone: f.phone || null, p_service_type: f.service_type || null,
    p_base_fee: f.base_fee == null || f.base_fee === "" ? null : Number(f.base_fee),
    p_per_km_fee: f.per_km_fee == null || f.per_km_fee === "" ? null : Number(f.per_km_fee),
    p_is_always_available: !!f.is_always_available, p_notes: f.notes || null,
  }),
  update: (id, f) => rpc("update_rider", {
    p_rider_id: id, p_name: f.name, p_phone: f.phone || null, p_service_type: f.service_type || null,
    p_base_fee: f.base_fee == null || f.base_fee === "" ? null : Number(f.base_fee),
    p_per_km_fee: f.per_km_fee == null || f.per_km_fee === "" ? null : Number(f.per_km_fee),
    p_is_always_available: !!f.is_always_available, p_active: f.active ?? true, p_notes: f.notes || null,
  }),
  upsertAvailability: (riderId, day, start, end, notes) => rpc("upsert_rider_availability", {
    p_rider_id: riderId, p_day_of_week: day, p_start_time: start || null, p_end_time: end || null, p_notes: notes || null,
  }),
  deleteAvailability: (riderId, day) => rpc("delete_rider_availability", { p_rider_id: riderId, p_day_of_week: day }),
  createErrand: (riderId, f) => rpc("create_errand", {
    p_rider_id: riderId, p_date: f.date || null, p_errand_type: f.errand_type || null,
    p_description: f.description || null,
    p_budget: f.budget == null || f.budget === "" ? null : Number(f.budget),
    p_order_id: f.order_id || null, p_notes: f.notes || null,
  }),
  updateErrand: (id, riderId, f) => rpc("update_errand", {
    p_errand_id: id, p_rider_id: riderId, p_date: f.date || null, p_errand_type: f.errand_type || null,
    p_status: f.status || null, p_description: f.description || null,
    p_budget: f.budget == null || f.budget === "" ? null : Number(f.budget),
    p_actual_cost: f.actual_cost == null || f.actual_cost === "" ? null : Number(f.actual_cost),
    p_expense_receipt_id: f.expense_receipt_id || null, p_order_id: f.order_id || null,
    p_notes: f.notes || null,
  }),
  removeErrand: (id) => rpc("soft_delete_errand", { p_errand_id: id }),
  ordersLookup: (signal) => selectAll("orders", { eq: { active: true }, order: { column: "created_at", ascending: false }, columns: "id, order_number", signal }),
  receiptsLookup: (signal) => selectAll("expense_receipts", { eq: { active: true }, order: { column: "date", ascending: false }, columns: "id, or_invoice_number, vendor_name, date", signal }),
};